import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import Loading from '../../components/common/Loading';
import Notification from '../../components/common/Notification';
import Tabs from '../../components/common/Tabs';
import ProfileInfo from '../../components/module/user/profile/ProfileInfo';
import UserProfileSidebar from '../../components/module/user/profile/UserProfileSidebar';
import { PAGE_IDS } from '../../constants/permissions';
import { useNotification } from '../../hooks/useNotification';
import type { UserProfileSidebarData } from '../../services/types/userProfile.types';
import { menuService } from '../../services/menu.service';

const DEFAULT_TABS: { label: string; value: string }[] = [
  { label: 'Profile Info', value: 'profile-info' },
  { label: 'Personal Info', value: 'personal-info' },
  { label: 'Contact Info', value: 'contact-info' },
  { label: 'Qualifications', value: 'qualifications' },
  { label: 'Employment History', value: 'employment-history' },
  { label: 'Documents', value: 'documents' },
];

const UserProfile: React.FC = () => {
  const [tabs, setTabs] = useState<{ label: string; value: string }[]>([]);
  const [activeTab, setActiveTab] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<UserProfileSidebarData | null>(null);
  const { notification, hideNotification, showNotification } = useNotification();
  const handleNotify = useCallback((msg: string, sev: 'success' | 'error') => showNotification(msg, sev), [showNotification]);

  useEffect(() => {
    const loadTabs = async () => {
      try {
        setIsLoading(true);
        const response = await menuService.getTabs(PAGE_IDS.USER_PROFILE);
        const raw = response.data.data ?? [];
        if (raw.length >= 6) {
          const mappedTabs = raw.map((tab) => ({
            label: tab.name,
            value: String(tab.menuId),
          }));
          setTabs(mappedTabs);
          setActiveTab(mappedTabs[0]?.value ?? '');
        } else {
          setTabs(DEFAULT_TABS.map((t) => ({ ...t })));
          setActiveTab(DEFAULT_TABS[0].value);
        }
      } catch (error) {
        console.error('Error fetching tabs:', error);
        setTabs(DEFAULT_TABS.map((t) => ({ ...t })));
        setActiveTab(DEFAULT_TABS[0].value);
      } finally {
        setIsLoading(false);
      }
    };

    void loadTabs();
  }, []);

  const renderContent = () => {
    const idx = tabs.findIndex((t) => t.value === activeTab);
    if (idx === 0) {
      return <ProfileInfo setSummary={setSummaryData} onNotify={handleNotify} />;
    }
    if (idx > 0) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography sx={{ fontSize: '15px', color: '#475467' }}>
            {tabs[idx]?.label ?? 'This tab'} is not available yet.
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box className="page-container" sx={{ flex: 1, overflowY: 'auto' }}>
        {isLoading || !activeTab ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Loading />
          </Box>
        ) : (
          <Box flex={1}>
            <Typography className="page-title" mb={3}>
              User Profile
            </Typography>
            <Box mb={3}>
              <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} useRouter={false} />
            </Box>
            {renderContent()}
          </Box>
        )}
      </Box>

      {summaryData && <UserProfileSidebar data={summaryData} />}

      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default UserProfile;
